import { defineStore } from "pinia";
import {
  handleApiResponse,
  handleError,
  handleImageResponse,
  clearAllCookies,
  saveToCookie,
  getCookie,
} from "@/utils/common.js";
import {
  LOGIN,
  REGISTER,
  VERIFY_EMAIL,
  REGISTER_VERIFY_EMAIL,
  USER_PROFILE,
  GET_STATES,
  GET_TIMEZONES,
  ADDRESS,
  FORGOT_PASSWORD,
  LOGOUT,
  PROFILE,
  ERROR_MESSAGE,
  GET_COUNTRIES,
  UPDATE_ADDRESS,
  REFRESH_TOKEN,
  REGISTER_USER_INCAPACITATION,
} from "@/api/apiRoute";
import { nonAuthData, authData } from "@/api/apiService.js";
import { ROUTE_PATHS } from "@/router/constants";
import { errorMessage } from "@/utils/errorMessages.js";

export const useUserStore = defineStore("userStore", {
  state: () => ({
    errorDisplayDuration: 4000,
    isFromRegistration: getCookie("isFromRegistration", false, "boolean"),
    showVerifyEmail: getCookie("showVerifyEmail", true, "boolean"),
    showOtp: getCookie("showOtp", false, "boolean"),
    showEmailSuccess: getCookie("showEmailSuccess", false, "boolean"),
    email: getCookie("email", ""),
    forgotPasswordFlow: getCookie("forgotPasswordFlow", false, "boolean"),
    loading: false,
    loginUserId: getCookie("loginUserId", null, "number"),
    fullName: getCookie("fullName", ""),
    firstName: getCookie("firstName", ""),
    expireTime: getCookie("expireTime", ""),
    accessToken: getCookie("accessToken", ""),
    refreshToken: getCookie("refreshToken", ""),
    errorMessage: "",
    profileImage: null,
    imageVersion: 0,
    allErrorMessages: [],
    welcomeModal: getCookie("welcomeModal", false, "boolean"),
    userAddressData: null,
    isInviteMemberModalOpen: false,
    statesList: [],
    countriesList: [],
    timeZoneList: [],
    inviterName: getCookie("inviterName") || "",
    invitationData: getCookie("invitationData") || {},
    hasInvitation: getCookie("hasInvitation") || false,
    progressValue: getCookie("progressValue") || 25,
    loginResponse: getCookie("loginResponse") || {},
    addressData: getCookie("addressData") || {},
    emailSuccessView: false,
    shouldShowTutorial: getCookie("shouldShowTutorial", false, "boolean"),
    subscriptionMsgPopUp: getCookie("subscriptionMsgPopUp", false, "boolean"),
    isIncapacited: getCookie("isIncapacited", ""),
    planId: getCookie("planId", null, "number"),
    promoCode: "",
    affiliateCode: getCookie("affiliateCode", ""),
    isRegisteredKey: getCookie("isRegisteredKey", false, "boolean"),
    agentUserId: null,
    licensesName: getCookie("licensesName", [], "array")
  }),

  getters: {
    loginDetails: (state) => getCookie("loginResponse") || state.loginResponse,
    userFirstName: (state) => state.firstName,
  },

  actions: {
    setInvitation(data) {
      this.invitationData = data;
      this.inviterName = data.sourceDisplay || "";
      this.hasInvitation = Boolean(data.invitationCode);
      this.progressValue = 25;
      this.saveStateToCookies();
    },
    clearInvitation() {
      this.invitationData = {};
      this.inviterName = "";
      this.hasInvitation = false;
      this.progressValue = 0;
      this.saveStateToCookies();
    },
    updateProgress(value) {
      this.progressValue = value;
      this.saveStateToCookies();
    },
    saveStateToCookies() {
      saveToCookie("isFromRegistration", this.isFromRegistration);
      saveToCookie("showVerifyEmail", this.showVerifyEmail);
      saveToCookie("showOtp", this.showOtp);
      saveToCookie("showEmailSuccess", this.showEmailSuccess);
      saveToCookie("email", this.email);
      saveToCookie("forgotPasswordFlow", this.forgotPasswordFlow);
      saveToCookie("fullName", this.fullName);
      saveToCookie("firstName", this.firstName);
      saveToCookie("welcomeModal", this.welcomeModal);
      saveToCookie("loginUserId", this.loginUserId);
      saveToCookie("inviterName", this.inviterName);
      saveToCookie("hasInvitation", this.hasInvitation);
      saveToCookie("invitationData", this.invitationData);
      saveToCookie("expireTime", this.expireTime);
      saveToCookie("accessToken", this.accessToken);
      saveToCookie("refreshToken", this.refreshToken);
      saveToCookie("shouldShowTutorial", this.shouldShowTutorial);
      saveToCookie("subscriptionMsgPopUp", this.subscriptionMsgPopUp);
      saveToCookie("isIncapacited", this.isIncapacited);
      saveToCookie("planId", this.planId);
      saveToCookie("affiliateCode", this.affiliateCode);
      saveToCookie("isRegisteredKey", this.isRegisteredKey);
      saveToCookie("licensesName", this.licensesName);
    },
    setRegisteredKey(value) {
      this.isRegisteredKey = value;
      this.saveStateToCookies();
    },
    clearErrorMessage() {
      this.errorMessage = "";
    },
    setErrorMessage(message) {
      this.errorMessage =
        errorMessage[message] || message || "An error occurred";
    },
    forgotPasswordPageStates() {
      this.showVerifyEmail = true;
      this.showOtp = false;
      this.showEmailSuccess = false;
      this.email = "";
      this.forgotPasswordFlow = false;
      this.loading = false;
      this.saveStateToCookies();
    },
    showError(error) {
      this.setErrorMessage(error?.response?.data?.error);
      setTimeout(() => {
        this.clearErrorMessage();
      }, this.errorDisplayDuration);
      handleError(error);
      throw error;
    },
    setShouldShowTutorial(value) {
      this.shouldShowTutorial = value;
      this.saveStateToCookies();
    },
    async getAllErrorMessages() {
      try {
        const response = await nonAuthData.get(ERROR_MESSAGE);
        const errorMessages = response.data.data;
        this.allErrorMessages = errorMessages;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      }
    },
    async registerUser(payload) {
      try {
        this.loading = true;
        const response = await nonAuthData.post(REGISTER, payload);
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async registerVerifyUser(payload) {
      try {
        this.loading = true;
        const response = await nonAuthData.post(REGISTER_VERIFY_EMAIL, payload);
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async verifySignUp(payload) {
      try {
        this.loading = true;
        const response = await nonAuthData.post(VERIFY_EMAIL, payload);
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async createAddress(payload) {
      try {
        this.loading = true;
        const response = await authData.post(ADDRESS, payload);
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async loginUser(payload) {
      this.loading = true;
      try {
        const response = await nonAuthData.post(LOGIN, payload);
        const data = response.data.data;

        // Convert to timestamp
        this.expireTime = new Date(data.expires).getTime();
        this.accessToken = data.access;
        this.refreshToken = data.refresh;

        saveToCookie("loginResponse", data);

        this.loginResponse = data;
        this.licensesName = data.licenses || [];
        this.saveStateToCookies();

        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },

    async refreshUserToken(payload) {
      this.loading = true;
      try {
        const response = await nonAuthData.post(REFRESH_TOKEN, payload);
        const data = response?.data?.data;

        // Convert to timestamp
        this.expireTime = new Date(data.expires).getTime();
        this.accessToken = data.access;
        this.refreshToken = data.refresh;

        saveToCookie("loginResponse", data);

        this.loginResponse = data;
        this.licensesName = data.licenses || [];
        this.saveStateToCookies();

        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },

    async forgotPassword(payload) {
      this.loading = true;
      try {
        const response = await nonAuthData.post(FORGOT_PASSWORD, payload);
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async updateProfile(payload) {
      this.loading = true;
      try {
        const response = await authData.patch(USER_PROFILE, payload);
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async updateAddress(id, payload) {
      this.loading = true;
      try {
        const response = await authData.patch(UPDATE_ADDRESS(id), payload);
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async getStates(url = GET_STATES) {
      this.loading = true;
      try {
        const response = await authData.get(url);
        this.statesList = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async getCountries() {
      if (this.countriesList && this.countriesList.length > 0) {
        return this.countriesList;
      }
      this.loading = true;
      try {
        const response = await authData.get(GET_COUNTRIES);
        this.countriesList = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async getTimeZones(country = null) {
      if (this.timeZoneList && this.timeZoneList.length > 0) {
        return this.timeZoneList;
      }
      this.loading = true;
      try {
        const response = await authData.get(GET_TIMEZONES(country));
        this.timeZoneList = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async getUserAddress(payload) {
      try {
        this.loading = true;
        const response = await authData.get(ADDRESS, payload);
        this.userAddressData = response.data.data?.length
          ? response.data.data?.[0]
          : null;
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
        async logout(homeToLogin = true) {
      try {
        this.loading = true;
        const response = await authData.post(LOGOUT);

        clearAllCookies(["isRegisteredKey"]);

        if (response && homeToLogin) {
          window.location.href = ROUTE_PATHS.LOGIN;
        }
        // Clear login response
        this.loginResponse = {};
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async getProfileImage() {
      this.loading = true;
      try {
        const cacheBuster = `?t=${Date.now()}`;
        const response = await authData.get(`${PROFILE}${cacheBuster}`, {
          responseType: "blob",
        });
        const imageUrl = handleImageResponse(response);
        if (imageUrl) {
          if (this.profileImage?.startsWith("blob:")) {
            URL.revokeObjectURL(this.profileImage);
          }
          this.profileImage = imageUrl;
          this.imageVersion += 1;
          return imageUrl;
        }
        return null;
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
    async registerUserIncapacitation(payload) {
      try {
        this.loading = true;
        const response = await nonAuthData.post(
          REGISTER_USER_INCAPACITATION,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        return handleError(error);
      } finally {
        this.loading = false;
      }
    },
  },
});
